import {
  CalendarApi,
  createPlugin,
  EventInput,
} from '@fullcalendar/core/index.js';
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from '@fullcalendar/list';
import FullCalendar from '@fullcalendar/react';
import {
  Card,
  Skeleton,
  SvgIcon,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material';
import {
  RiArrowRightLine,
  RiMapPinLine,
  RiShipLine,
  RiTimeLine,
} from '@remixicon/react';
import dayjs, { Dayjs } from 'dayjs';
import { useMemo, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useLanguage } from '../../contexts/language';
import {
  AdHocBadge,
  JourneyEntityStatusBadge,
  JourneyStatusBadge,
} from '../../pages/admin/Journeys/JourneyStatusBadges';
import {
  generateCalendarEventId,
  isJourneyPending,
} from '../../pages/admin/Journeys/utils';
import { ContractListItem } from '../../types/contract';
import {
  AllowedCalendarRangeTypes,
  AllowedCalendarView,
  EntityStatus,
  JourneyStatus,
} from '../../types/core';
import { JourneyPlaceholder } from '../../types/journey';
import { PortSelectItem } from '../../types/port';
import { getFirstStartingMonthDate, MONTHS, WEEKDAYS } from '../../utils/date';
import DateSelector from './DateSelector';
import Filters from './Filters';

export interface CustomCalendarProps {
  rangeType: AllowedCalendarRangeTypes;
  contracts: ContractListItem[];
  isFetchingContracts: boolean;
  selectedContractId: string | null;
  onSelectedContractIdChange: (newSelectedContractId: string | null) => void;
  journeyPlaceholders: JourneyPlaceholder[];
  isLoadingJourneyPlaceholders: boolean;
  selectedDate: Dayjs;
  onSelectedDateChange: (newSelectedDate: Dayjs) => void;
  selectedView: AllowedCalendarView;
  onSelectedViewChange: (newView: AllowedCalendarView) => void;
  journeyStatusFilters: JourneyStatus[];
  onJourneyStatusFiltersChange: (newFilters: JourneyStatus[]) => void;
  entityStatusFilters: EntityStatus[];
  onEntityStatusFiltersChange: (newFilters: EntityStatus[]) => void;
  portsMap: Record<string, PortSelectItem>;
}

const CustomCalendar = (customCalendarProps: CustomCalendarProps) => {
  const {
    rangeType,
    selectedContractId,
    journeyPlaceholders,
    isLoadingJourneyPlaceholders,
    portsMap,
    selectedDate,
    onSelectedDateChange,
    selectedView,
    onSelectedViewChange,
    entityStatusFilters,
    journeyStatusFilters,
    contracts,
    isFetchingContracts,
    onEntityStatusFiltersChange,
    onJourneyStatusFiltersChange,
    onSelectedContractIdChange,
  } = customCalendarProps;

  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const calendarApi = useRef<CalendarApi | null>(null);

  const isMonthlyRangeType = rangeType === AllowedCalendarRangeTypes.Monthly;

  const displayedJourneyEvents = useMemo(() => {
    const journeyEvents: EventInput[] = [];

    if (isLoadingJourneyPlaceholders) {
      if (selectedView === AllowedCalendarView.ListMonth) {
        return journeyEvents;
      }

      const dateIterator = selectedDate.toDate();
      const lastDate = new Date(dateIterator);

      if (isMonthlyRangeType) {
        dateIterator.setDate(1);
        lastDate.setDate(1);
        lastDate.setMonth(lastDate.getMonth() + 1);
        lastDate.setDate(0);
      }

      for (
        dateIterator;
        dateIterator <= lastDate;
        dateIterator.setDate(dateIterator.getDate() + 1)
      ) {
        journeyEvents.push({
          date: new Date(dateIterator),
          className: 'loading-journey-placeholders',
          allDay: true,
        });
      }
      return journeyEvents;
    }

    journeyPlaceholders.forEach(placeholder => {
      const placeholderDate = dayjs(`${placeholder.day}T${placeholder.time}`);
      if (
        !placeholderDate.isValid() ||
        !placeholderDate.isSame(
          selectedDate,
          isMonthlyRangeType ? 'month' : 'day',
        )
      ) {
        return;
      }

      const placeholderId = generateCalendarEventId(placeholder);
      const journeyEvent: EventInput = {
        id: placeholderId,
        title: `${portsMap[placeholder.departurePortId].portInstance.islandNames[language]} > ${portsMap[placeholder.arrivalPortId].portInstance.islandNames[language]}`,
        start: placeholderDate.toDate(),
        classNames: [
          'cursor-pointer',
          `journey-status-${placeholder.journeyStatus.toLowerCase()}`,
          isJourneyPending(
            placeholder.journeyStatus,
            placeholder.day,
            placeholder.time,
          )
            ? 'pending'
            : '',
        ].filter(Boolean),
        extendedProps: {
          formattedTime: placeholder.time.split(':').slice(0, 2).join(':'),
          placeholder,
        },
      };

      // Filters out non desired journeys
      if (
        (entityStatusFilters.length > 0 &&
          !entityStatusFilters.includes(placeholder.entityStatus)) ||
        (journeyStatusFilters.length > 0 &&
          !journeyStatusFilters.includes(placeholder.journeyStatus))
      ) {
        return;
      }

      journeyEvents.push(journeyEvent);
    });

    return journeyEvents;
  }, [
    isLoadingJourneyPlaceholders,
    journeyPlaceholders,
    selectedView,
    selectedDate,
    isMonthlyRangeType,
    portsMap,
    language,
    entityStatusFilters,
    journeyStatusFilters,
  ]);

  const { customDateLocalizationPlugin, formatDayMonthYear, formatMonthYear } =
    useMemo(() => {
      const formatMonth = (date: Date) =>
        t(`months.${MONTHS[date.getMonth()]}`);
      const formatMonthYear = (date: Date) =>
        `${formatMonth(date)} ${date.getFullYear()}`;
      const formatDayMonthYear = (date: Date) =>
        `${date.getDate()} ${formatMonthYear(date)}`;
      return {
        customDateLocalizationPlugin: createPlugin({
          name: 'CustomDateLocalizationPlugin',
          cmdFormatter: (cmd, arg) => {
            switch (cmd) {
              case 'list-day':
                return `${t(`weekdays.${WEEKDAYS[arg.date.marker.getDay()]}.full`)}, ${t(`months.${MONTHS[arg.date.marker.getMonth()]}`)} ${arg.date.day}`;
            }
            return arg.date.marker.toString();
          },
        }),
        formatDayMonthYear,
        formatMonthYear,
      };
    }, [t]);

  return (
    <div>
      <Card className="py-[10px] px-[20px] mb-4">
        <div className="grid gap-3 md:gap-8 grid-cols-[min-content_1fr_min-content] grid-rows-[min-content_min-content] lg:grid-cols-[min-content_min-content_1fr_min-content] lg:grid-rows-[min-content] items-center">
          <DateSelector
            todayLabel={t('content.today')}
            onPrev={() => {
              calendarApi.current?.incrementDate(
                isMonthlyRangeType ? { months: -1 } : { days: -1 },
              );
            }}
            onToday={() => calendarApi.current?.gotoDate(new Date())}
            onNext={() => {
              calendarApi.current?.incrementDate(
                isMonthlyRangeType ? { months: 1 } : { days: 1 },
              );
            }}
          />
          <div
            className={`col-start-2 ${isMonthlyRangeType ? 'col-span-1' : 'col-span-2 lg:col-span-1'} whitespace-nowrap text-xl/[32px] md:text-2xl/[40px] ml-4`}
          >
            {isMonthlyRangeType
              ? formatMonthYear(selectedDate.toDate())
              : formatDayMonthYear(selectedDate.toDate())}
          </div>
          <Filters
            className={`col-start-1 row-start-2 col-span-full lg:col-start-auto lg:row-start-auto  ${isMonthlyRangeType ? 'lg:col-span-1' : 'lg:col-span-2'}`}
            contracts={contracts}
            entityStatusFilters={entityStatusFilters}
            isFetchingContracts={isFetchingContracts}
            isLoadingJourneyPlaceholders={isLoadingJourneyPlaceholders}
            journeyStatusFilters={journeyStatusFilters}
            onEntityStatusFiltersChange={onEntityStatusFiltersChange}
            onJourneyStatusFiltersChange={onJourneyStatusFiltersChange}
            onSelectedContractIdChange={onSelectedContractIdChange}
            selectedContractId={selectedContractId}
          />

          {isMonthlyRangeType && (
            <ToggleButtonGroup
              value={selectedView}
              exclusive
              onChange={(_, newValue: AllowedCalendarView) => {
                if (newValue && calendarApi.current) {
                  calendarApi.current.changeView(
                    newValue,
                    selectedDate.toDate(),
                  );
                  onSelectedViewChange(newValue);
                }
              }}
            >
              <ToggleButton value={AllowedCalendarView.ListMonth}>
                {t('content.list')}
              </ToggleButton>
              <ToggleButton value={AllowedCalendarView.DayGridMonth}>
                {t('content.month')}
              </ToggleButton>
            </ToggleButtonGroup>
          )}
        </div>
      </Card>
      {/* All overriding styles for this usage of "Fullcalendar" are defined in the '/src/index.css' file */}
      <Card>
        <div className="iots-admin-journeys-calendar max-w-full overflow-x-hidden">
          <FullCalendar
            plugins={[listPlugin, dayGridPlugin, customDateLocalizationPlugin]}
            initialView={selectedView}
            initialDate={selectedDate.toDate()}
            events={displayedJourneyEvents}
            viewDidMount={({ view }) => {
              if (view.calendar !== calendarApi.current) {
                calendarApi.current = view.calendar;
              }
            }}
            height="auto"
            headerToolbar={false}
            eventTimeFormat={{
              hour: '2-digit',
              minute: '2-digit',
              hour12: false,
              meridiem: false,
            }}
            listDayFormat="list-day"
            listDaySideFormat={false}
            views={{
              [AllowedCalendarView.DayGridMonth]: {
                dayMaxEvents: 3,
              },
            }}
            dayPopoverFormat="list-day"
            datesSet={event => {
              if (
                (event.view.type as AllowedCalendarView) ===
                AllowedCalendarView.DayGridMonth
              ) {
                /* The selected date is always set to first day of the month
                covered by the view.
                If the "start" date doesn't match the first day of the month,
                that means the "dayGridMonth" view has been selected and the 
                "start" date matches the last days of the previous month visible
                in the calendar */
                onSelectedDateChange(
                  dayjs(getFirstStartingMonthDate(event.start)),
                );
                return;
              }
              onSelectedDateChange(dayjs(event.start));
            }}
            eventClick={({ event }) => {
              const placeholder = event.extendedProps
                .placeholder as JourneyPlaceholder;
              if (placeholder.journeyId) {
                void navigate(`/admin/journeys/${placeholder.journeyId}`);
              } else {
                void navigate(`/admin/journeys/placeholder`, {
                  state: placeholder,
                });
              }
            }}
            eventContent={({ view, event }) => {
              const placeholder = event.extendedProps
                .placeholder as JourneyPlaceholder;
              if (!placeholder) {
                return (
                  <div className="col-span-3">
                    <Skeleton variant="rounded" />
                  </div>
                );
              }
              if (
                ![
                  AllowedCalendarView.ListDay,
                  AllowedCalendarView.ListMonth,
                ].includes(view.type as AllowedCalendarView)
              ) {
                return true;
              }
              return (
                <div className="grid gap-2 md:grid-cols-3 md:items-center">
                  <div className="flex items-center gap-2">
                    <SvgIcon component={RiTimeLine} />
                    <span>{event.extendedProps.formattedTime as string}</span>
                    <JourneyStatusBadge
                      journeyStatus={placeholder.journeyStatus}
                      journeyDay={placeholder.day}
                      journeyTime={placeholder.time}
                      small
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <SvgIcon component={RiMapPinLine} />
                    <Tooltip
                      title={portsMap[placeholder.departurePortId].label}
                    >
                      <span className="font-bold">
                        {
                          portsMap[placeholder.departurePortId].portInstance
                            .islandNames[language]
                        }
                      </span>
                    </Tooltip>
                    <SvgIcon
                      className="h-[12px] w-[12px]"
                      component={RiArrowRightLine}
                    />
                    <Tooltip title={portsMap[placeholder.arrivalPortId].label}>
                      <span className="font-bold">
                        {
                          portsMap[placeholder.arrivalPortId].portInstance
                            .islandNames[language]
                        }
                      </span>
                    </Tooltip>
                  </div>

                  <div className="flex justify-between items-center">
                    <span className="flex items-center gap-2">
                      <SvgIcon
                        className="h-[20px] w-[20px]"
                        component={RiShipLine}
                      />
                      {t(`service-types.${placeholder.serviceType}.name`)}
                    </span>
                    <div className="flex gap-2">
                      {!placeholder.routeId && <AdHocBadge small />}
                      <JourneyEntityStatusBadge
                        entityStatus={placeholder.entityStatus}
                        small
                      />
                    </div>
                  </div>
                </div>
              );
            }}
            noEventsText={
              !selectedContractId
                ? t('content.contract-selection-request')
                : isLoadingJourneyPlaceholders
                  ? t('content.loading-journeys')
                  : t('content.no-journeys-found')
            }
          />
        </div>
      </Card>
    </div>
  );
};

export default CustomCalendar;
